import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import FestivalList from './pages/FestivalList';
import FestivalEditor from './pages/FestivalEditor';
import FestivalEdit from './pages/FestivalEdit';
import FestivalView from './pages/FestivalView';
import UserManagement from './pages/UserManagement';
import FeedbackList from './pages/FeedbackList';
import Parameters from './pages/Parameters';
import FriendFinder from './pages/FriendFinder';
import UserDashboard from './pages/UserDashboard';

import VerifyEmail from './pages/VerifyEmail';
import AcceptInvitation from './pages/AcceptInvitation';

function App() {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  // Check if user has admin role
  const isAdmin = user?.roles?.includes('administrator') || user?.roles?.includes('super_administrator');

  if (loading) {
    return (
      <div className="loading">
        <div>Loading...</div>
      </div>
    );
  }

  // Email verification route - accessible with or without authentication
  if (location.pathname.startsWith('/verify-email')) {
    return (
      <Routes>
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
      </Routes>
    );
  }

  // Accept invitation route - accessible with or without authentication (shows login prompt if needed)
  if (location.pathname.startsWith('/accept-invite')) {
    return (
      <Routes>
        <Route path="/accept-invite/:token" element={<AcceptInvitation />} />
      </Routes>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  // Redirect /admin root and /admin/login to /user
  if (location.pathname === '/admin' || location.pathname === '/admin/' || location.pathname === '/admin/login') {
    return <Navigate to="/user" replace />;
  }

  // Protect admin routes - redirect non-admins to user dashboard
  if (location.pathname.startsWith('/admin') && !isAdmin) {
    return <Navigate to="/user?error=admin_required" replace />;
  }

  // User routes (full-screen, no admin sidebar)
  if (location.pathname.startsWith('/user')) {
    return (
      <Routes>
        <Route path="/user" element={<UserDashboard view="how-it-works" />} />
        <Route path="/user/how-it-works" element={<UserDashboard view="how-it-works" />} />
        <Route path="/user/festivals-nearby" element={<UserDashboard view="festivals-nearby" />} />
        <Route path="/user/festivals" element={<UserDashboard view="your-festivals" />} />
        <Route path="/user/create" element={<UserDashboard view="create-festival" />} />
        <Route path="/user/account" element={<UserDashboard view="account" />} />
        <Route path="/user/qr-code" element={<UserDashboard view="qr-code" />} />
        <Route path="/user/festivals/:festivalId/editor" element={<FestivalEditor />} />
      </Routes>
    );
  }

  // Admin routes (with header and sidebar)
  return (
    <div className="app">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="content-area">
          <Routes>
            <Route path="/admin/dashboard" element={<Dashboard />} />
            <Route path="/admin/festivals" element={<FestivalList />} />
            <Route path="/admin/festivals/:festivalId/view" element={<FestivalView />} />
            <Route path="/admin/festivals/:festivalId/edit" element={<FestivalEdit />} />
            <Route path="/admin/festivals/:festivalId/editor" element={<FestivalEditor />} />
            <Route path="/admin/users" element={<UserManagement />} />
            <Route path="/admin/feedback" element={<FeedbackList />} />
            <Route path="/admin/friend-finder" element={<FriendFinder />} />
            <Route path="/admin/parameters" element={<Parameters />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default App;
